import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import SalarySubNav from './SalarySubNav';
import { apiGet, apiSend } from './api';
import type { EmployerProfile as Profile } from './types';

const EmployerProfile: React.FC = () => {
  const { sessionToken } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet<{ profile: Profile }>('/employer-profile', sessionToken);
        setProfile(res.profile);
      } catch (e: any) { setErr(e.message); }
    })();
  }, [sessionToken]);

  const save = async () => {
    if (!profile) return;
    try {
      setErr(''); setMsg('');
      await apiSend('/employer-profile', 'PUT', profile, sessionToken);
      setMsg('Saved. New pay stubs will use this info. Use "Regenerate" on existing stubs to update them.');
    } catch (e: any) { setErr(e.message); }
  };

  if (!profile) {
    return (
      <div className="salary-module">
        <h2>Employer Profile</h2>
        <SalarySubNav />
        {err && <div className="error-banner">{err}</div>}
        <p>Loading…</p>
      </div>
    );
  }

  const field = <K extends keyof Profile>(label: string, key: K, type = 'text') => (
    <div className="form-row">
      <label>{label}</label>
      <input
        type={type}
        value={(profile[key] as string) || ''}
        onChange={(e) => setProfile({ ...profile, [key]: e.target.value })}
      />
    </div>
  );

  return (
    <div className="salary-module">
      <h2>Employer Profile</h2>
      <SalarySubNav />
      {err && <div className="error-banner">{err}</div>}
      {msg && <div className="error-banner" style={{ background: '#d4edda', color: '#155724' }}>{msg}</div>}

      <div className="salary-card" style={{ maxWidth: 640 }}>
        <p style={{ color: '#666', fontSize: '0.9rem', marginTop: 0 }}>
          This info appears on the pay stub header and T4 filings. Editing it does NOT
          update already-generated stubs — use "Regenerate" on a payment to refresh its PDF.
        </p>
        {field('Legal name', 'legal_name')}
        {field('Address line 1', 'address_line1')}
        {field('Address line 2', 'address_line2')}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.6rem' }}>
          {field('City', 'city')}
          {field('Province', 'province')}
          {field('Postal code', 'postal_code')}
        </div>
        {field('Business Number (CRA)', 'business_number')}
        {field('Phone', 'phone')}
        {field('Email', 'email', 'email')}

        <div style={{ marginTop: '1rem', textAlign: 'right' }}>
          <button className="salary-btn" onClick={save}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default EmployerProfile;
